import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Icon, Intent, Menu, MenuItem, NavbarDivider, Position} from "@blueprintjs/core";
import {IconNames} from '@blueprintjs/icons'
import {Popover2} from '@blueprintjs/popover2';

import {SendToMeOptions} from '../../../../store/actions/reportResultActions';
import {updateReport} from '../../../../store/actions/reportFormActions';
import {useCurrentUser, useExecuteReport, useReportIsDirty} from '../../../../hooks';
import {toReportEntity} from '../../../../utils/report';
import {IState} from '../../../../store/reducers';
import {ApiStatus, Report} from '../../../../models';
import {useUpdateBlocks} from '../../../blocks';
import SendJustToMeDialog from '../../../dialogs/SendJustToMeDialog';
import {isReport} from '../../../dialogs/SendJustToMeDialog/utils';
import ShareReport from './ShareReport';
import {getRunButtonText} from './utils';

interface ReportHeaderRightProps {
}

interface ReportExecutionMenuProps {
  onExecute: (value?: any) => void;
  onShowSendToMe: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const ReportExecutionMenuContent = ({
  onExecute,
  onShowSendToMe,
}: ReportExecutionMenuProps) => {
  return (
    <Menu>
      <MenuItem
        text="Send Just to Me"
        onClick={onShowSendToMe}
      />
      <MenuItem
        text="Run Report"
        onClick={onExecute}
      />
    </Menu>
  )
}

export const ReportExecutionMenu = ({
  onExecute,
  onShowSendToMe,
  disabled = false,
  loading = false,
}: ReportExecutionMenuProps) => {
  return (
    <Popover2
      position={Position.BOTTOM_LEFT}
      content={
        <ReportExecutionMenuContent
          onExecute={onExecute}
          onShowSendToMe={onShowSendToMe}
        />
      }
    >
      <Button
        large={false}
        minimal={true}
        outlined={true}
        icon={IconNames.PLAY}
        disabled={disabled}
        loading={loading}
        rightIcon={IconNames.CHEVRON_DOWN}
      />
    </Popover2>
  )
}

export const ReportHeaderRight = ({
}: ReportHeaderRightProps) => {
  const [showDialog, setShowDialog] = useState(false);

  const currentUser = useCurrentUser();
  const report = useSelector((state: IState) => state.reportForm.report);
  const updating = useSelector((state: IState) => state.reportForm.updatingStatus);
  const reportBlocks = useSelector((state: IState) => state.reportForm.blocks); // or `content`
  const readOnly = useSelector((state: IState) => state.reportForm.readOnly);
  const reportExists = !!report?.id;
  const {loading: loadingExecution, onExecute} = useExecuteReport(report?.uid as string, report?.name as string);
  const {isUpdating: isUpdatingBlocks, updateBlocks} = useUpdateBlocks();
  const {isDirty, isReportDirty, areBlocksDirty} = useReportIsDirty();
  const isUpdating = useMemo(() => updating === ApiStatus.LOADING, [updating]);
  const dispatch = useDispatch();

  const onShowSendToMe = () => {
    setShowDialog(true);
  }
  const onSend = (value: Partial<SendToMeOptions>) => {
    onExecute({
      send_to_me: true,
      ...value,
    })
  };

  const onSave = () => {
    /*
     * NOTE: this function is similar to `onSave` from ReportPageContainer.
     */
    const newReport = toReportEntity(report as any, reportBlocks as any || []);

    dispatch(updateReport(report?.uid as string, newReport));
    if (areBlocksDirty) {
      updateBlocks(reportBlocks as any || []);
    }
  }

  return (
    <>
      {
        reportExists && (
          <>
            {
              isReport(report as Report) ? (
                <>
                  <ReportExecutionMenu
                    onExecute={onExecute}
                    onShowSendToMe={onShowSendToMe}
                    disabled={readOnly}
                    loading={loadingExecution}
                  />
                  <Button
                    className="header__report__save-button"
                    text="Save"
                    onClick={onSave}
                    intent={Intent.PRIMARY}
                    loading={isUpdating || isUpdatingBlocks}
                    icon={IconNames.FLOPPY_DISK}
                    disabled={readOnly}
                  />
                </>
              ) : (
                <Button
                  text={getRunButtonText(report?.type)}
                  loading={loadingExecution}
                  onClick={onExecute}
                  icon={IconNames.PLAY}
                  disabled={readOnly}
                />
              )
            }
            <ShareReport uid={report?.uid as string} />
          </>
        )
      }
      {
        showDialog && (
          <SendJustToMeDialog
            destinations={report?.options_json?.destinations}
            show={showDialog}
            onSend={onSend}
            onClose={() => setShowDialog(false)}
          />
        )
      }
      <NavbarDivider/>
    </>
  );
}

export default ReportHeaderRight;
